import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-router-dom";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

// Set this only if the URL configured in Django differs.
const SUMMARY_PATH =
  process.env.REACT_APP_COMMUNICATION_UNREAD_PATH ||
  "/api/communication/notifications/summary/";

export const COMMUNICATION_UNREAD_CHANGED_EVENT =
  "legup:communication-unread-changed";

/**
 * Per-user unread message count for the Homebuyer, OSS and Lender sidebars.
 * Uses CommunicationNotification, never an organisation-wide aggregate.
 */
export default function useCommunicationUnreadCount() {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const location = useLocation();
  const [count, setCount] = React.useState(0);
  const controllerRef = React.useRef(null);

  const refresh = React.useCallback(async () => {
    if (!isAuthenticated) {
      setCount(0);
      return;
    }

    // Cancel an older request to avoid displaying an outdated count.
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    try {
      const token = await getAccessTokenSilently();
      if (controller.signal.aborted) return;

      const path = SUMMARY_PATH.startsWith("/")
        ? SUMMARY_PATH
        : `/${SUMMARY_PATH}`;
      const response = await fetch(`${API_BASE}${path}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        signal: controller.signal,
        cache: "no-store",
      });

      if (!response.ok) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("Could not load communication unread count:", response.status);
        }
        return;
      }

      const payload = await response.json();
      const raw = payload?.unread_messages_count ?? payload?.unread_count ?? 0;
      const parsed = Number(raw);

      if (!controller.signal.aborted) {
        setCount(Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : 0);
      }
    } catch (error) {
      if (error?.name !== "AbortError" && process.env.NODE_ENV !== "production") {
        console.warn("Could not refresh communication unread count:", error);
      }
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  React.useEffect(() => {
    if (!isAuthenticated) {
      setCount(0);
      return undefined;
    }

    void refresh();

    const updateWhenVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const updateOnFocus = () => void refresh();
    const updateOnMessageChange = () => void refresh();

    // Poll while the platform remains open; no dependency on dashboard reload.
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, 15000);

    window.addEventListener("focus", updateOnFocus);
    window.addEventListener(
      COMMUNICATION_UNREAD_CHANGED_EVENT,
      updateOnMessageChange,
    );
    document.addEventListener("visibilitychange", updateWhenVisible);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", updateOnFocus);
      window.removeEventListener(
        COMMUNICATION_UNREAD_CHANGED_EVENT,
        updateOnMessageChange,
      );
      document.removeEventListener("visibilitychange", updateWhenVisible);
      controllerRef.current?.abort();
    };
    // Refresh whenever the user changes route, including entering/leaving Communication.
  }, [isAuthenticated, refresh, location.pathname]);

  return count;
}
